// Firestore's storage size accounting (https://firebase.google.com/docs/firestore/storage-size).
// Browser-safe: SDK types (Timestamp, GeoPoint, DocumentReference) are recognized by shape, so
// values from either the client or the admin SDK are measured without importing them.

// Firestore rejects a document over 1 MiB.
export const FIRESTORE_DOCUMENT_SIZE_LIMIT = 1_048_576;

// A document's size: its name, its fields and 32 bytes of overhead.
export function documentSize(collectionId: string, docId: string, data: Record<string, unknown>) {
  return documentNameSize([collectionId, docId]) + mapEntriesSize(data) + 32;
}

// The size of a map's fields, each its name plus its value. Firestore stores no undefined field:
// the SDKs reject one unless told to drop it, and the web app writes JSON-shaped data, which has
// none.
export function mapEntriesSize(map: Record<string, unknown>) {
  let total = 0;
  for (const [key, value] of Object.entries(map)) {
    if (value !== undefined) total += stringSize(key) + valueSize(value);
  }
  return total;
}

// An undefined array entry is serialized as null, so it counts as one.
export function valueSize(value: unknown): number {
  if (value === null || value === undefined) return 1;
  if (typeof value === 'boolean') return 1;
  if (typeof value === 'number') return 8;
  if (typeof value === 'string') return stringSize(value);
  if (Array.isArray(value)) return value.reduce((total: number, entry) => total + valueSize(entry), 0);
  if (value instanceof Date) return 8;
  if (value instanceof Uint8Array) return value.length;
  if (typeof value === 'object') return objectSize(value);
  return 8;
}

export function stringSize(value: string) {
  return utf8ByteLength(value) + 1;
}

function documentNameSize(segments: string[]) {
  return segments.reduce((total, segment) => total + stringSize(segment), 0) + 16;
}

// A plain object is a map; only class instances are checked for an SDK type's shape, so a map
// whose fields happen to match one is still measured as a map.
function objectSize(value: object): number {
  const prototype = Object.getPrototypeOf(value);
  const isPlain = prototype === Object.prototype || prototype === null;
  if (!isPlain) {
    const candidate = value as Record<string, unknown>;
    if (typeof candidate.toMillis === 'function') return 8;
    if (typeof candidate.latitude === 'number' && typeof candidate.longitude === 'number') return 16;
    if (typeof candidate.path === 'string' && 'firestore' in candidate) {
      return documentNameSize(candidate.path.split('/'));
    }
  }
  return mapEntriesSize(value as Record<string, unknown>) + 32;
}

// UTF-8 bytes of a string, counted without encoding it. A lone surrogate encodes as the 3-byte
// replacement character.
function utf8ByteLength(value: string) {
  let bytes = 0;
  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && isLowSurrogate(value.charCodeAt(index + 1))) {
      bytes += 4;
      index++;
    } else bytes += 3;
  }
  return bytes;
}

function isLowSurrogate(code: number) {
  return code >= 0xdc00 && code <= 0xdfff;
}
